<template>
<div class="sidebar-dataset-documentation">
  <div class="sidebar-dataset-documentation__section-label font-section grey-7 mb-16">
    DATABASE DOCUMENTATION
  </div>

  <div
    v-if="databases.length > 0"
    class="chart-config__dataset mb-16">
    <label class="font-label">Dataset</label>
    <div class="relative">
      <SelectDropdown
        :inputOptions="databasesInputOptions"
        :inputValue="currentDatabaseId"
        @selected="updateDatabase" />
    </div>
  </div>

  <div class="flex mb-24">
    <div
      class="sidebar-dataset-documentation__dataset-description font-small"
      v-if="currentDatabase.description"
      v-html="$options.filters.mdWithP(currentDatabase.description)" />

    <div class="sidebar-dataset-documentation__schema-button">
      <SchemaVisualizerButton :databaseSlug="currentDatabase.slug" />
    </div>
  </div>

  <div class="sidebar-dataset-documentation__search-container mb-24">
    <label class="font-label">Search</label>
    <TextInput
      cssClass="font-small"
      placeholder="Search by name, type or description"
      @input="updateSearchQuery" />
  </div>

  <div
    class="sidebar-dataset-documentation__tables"
    v-if="currentDatabase.documentation">
    <div
      :key="table.name"
      class="sidebar-dataset-documentation__table-container"
      :class="{
        'display-none': !showTable(table)
      }"
      v-for="table in currentDatabase.documentation">

      <div class="sidebar-dataset-documentation__table-name-container mb-12 flex align-baseline">
        <TableIcon class="sidebar-dataset-documentation__table-icon mr-8" />

        <div
          class="sidebar-dataset-documentation__table-name weight-700"
          v-html="highlightSearchQuery(table.name)" />
      </div>

      <table class="info-table sidebar-dataset-documentation__table">
        <thead>
          <th class="font-label">Column</th>
          <th class="font-label">Type</th>
          <th class="font-label">Description</th>
        </thead>
        <tbody>
          <tr
            :key="column.name"
            v-for="column in table.columns"
            :class="{
              'display-none': !showColumn(column)
            }">
            <td
              class="font-small"
              v-html="highlightSearchQuery(column.name)" />
            <td
              class="font-small"
              v-html="highlightSearchQuery(column.type)" />
            <td
              class="font-small width-100"
              v-html="$options.filters.md(highlightSearchQuery(column.description))" />
          </tr>
        </tbody>
      </table>
    </div>
  </div>

  <CloseIcon
    class="sidebar-dataset-documentation__close-icon pointer grey-7"
    @click.prevent="close" />
</div>
</template>

<script>
import { mapGetters } from "vuex";
import SelectDropdown from "components/select-dropdown";
import TextInput from "components/shared/text-input";
import CloseIcon from "@images/font_awesome/x-light.svg";
import TableIcon from "@images/font_awesome/table-light.svg";
import SchemaVisualizerButton from "components/schema-visualizer-button.vue";

export default {
  components: {
    SelectDropdown,
    TextInput,
    CloseIcon,
    TableIcon,
    SchemaVisualizerButton
  },
  computed: {
    ...mapGetters("chart", ["databases", "currentDatabaseId", "currentDatabase"]),
    databasesInputOptions() {
      return this.databases.map(d => {
        return [
          d.name,
          d.id,
          "font_awesome/database-light.svg"
        ];
      });
    }
  },
  data() {
    return {
      searchQuery: null
    }
  },
  methods: {
    updateDatabase(databaseId) {
      this.$store.dispatch("chart/setCurrentDatabaseId", databaseId);
    },
    updateSearchQuery(newSearchQuery) {
      this.searchQuery = newSearchQuery;
    },
    highlightSearchQuery(copy) {
      if(this.searchQuery && copy.includes(this.searchQuery)) {
        return copy.replace(this.searchQuery, `<mark>${this.searchQuery}</mark>`);
      } else {
        return copy;
      }
    },
    showColumn(column) {
      if(this.searchQuery && this.searchQuery.length > 0) {
        return column.name.toLowerCase().includes(this.searchQuery) ||
          column.type.toLowerCase().includes(this.searchQuery) ||
          column.description.toLowerCase().includes(this.searchQuery);
      } else {
        return true;
      }
    },
    showTable(table) {
      if(this.searchQuery && this.searchQuery.length > 0) {
        return table.name.toLowerCase().includes(this.searchQuery) ||
          table.columns.some(column => this.showColumn(column));
      } else {
        return true;
      }
    },
    close() {
      this.$emit("close");
    }
  },
  mounted() {
    document.addEventListener("keydown", e => {
      if(e.key !== "Escape" && e.key !== "Esc") {
        return;
      }

      this.close();
    });
  }
}
</script>

<style lang="scss">
.sidebar-dataset-documentation {
  width: $px720;
  padding: $px24 $px24 $px48;
  overflow-y: scroll;
  max-height: 100vh;
  position: relative;

  background-color: $grey-10;

  &__dataset-description {
    @include breakpoint($md) {
      padding-right: $px24;
    }
  }

  &__table-container {
    &:not(:last-child) {
      margin-bottom: $px32;
    }
  }

  &__schema-button {
    display: none;

    @include breakpoint($md) {
      display: block;
      position: relative;
      z-index: 1;
    }
  }

  &__table {
    tr {
      &:nth-child(odd) {
        background-color: $grey-10;
      }

      &:nth-child(even) {
        background-color: $grey-9;
      }
    }

    td {
      padding-top: 10px;
      padding-bottom: 10px;
    }
  }

  &__schema-visualizer-link {
    @include font-small;
  }

  &__table-name {
    font-size: 20px;
    line-height: 20px;
    font-weight: 700;
  }

  &__close-icon {
    width: $px24;
    height: $px24;
    position: absolute;
    top: $px24;
    right: $px24;

    &:hover {
      color: $grey-6;
    }
  }

  &__table-icon {
    height: 12px;

    color: $grey-3;
  }
}
</style>
